// Daftar departemen / unit bisnis JAL Group — SUMBER TUNGGAL.
// Modul ini SENGAJA tanpa dependensi server (tidak import `pg`), supaya aman
// dipakai di client component maupun server.
// RBAC v2: DPP dihapus (tidak pernah terpakai); departemen user hanya bisa
// di-set admin lewat invite/edit — tidak ada lagi self-select atau nama custom.
//
// Struktur: HOLDING adalah induk (Director/Head pengawas lintas unit). Di bawahnya ada
// DIVISI internal — BISNIS & DEVELOPMENT, HR, FINANCE — yang BUKAN unit bisnis, lalu
// UNIT BISNIS: HAI-X, PADEL PARTY, UIM, UKP.
// HR & FINANCE: divisi internal yang datanya saling terisolasi (lihat
// CONFIDENTIAL_DEPARTMENTS di roles.ts).
export const DEPARTMENTS = ['HOLDING', 'BISNIS & DEVELOPMENT', 'HR', 'FINANCE', 'HAI-X', 'PADEL PARTY', 'UIM', 'UKP'] as const
export type Department = (typeof DEPARTMENTS)[number]

/** Divisi internal di bawah holding — bukan unit bisnis. Dipakai untuk pengelompokan UI. */
export const INTERNAL_DIVISIONS = ['BISNIS & DEVELOPMENT', 'HR', 'FINANCE'] as const

/** Unit bisnis (BU) di bawah holding. */
export const BUSINESS_UNITS = ['HAI-X', 'PADEL PARTY', 'UIM', 'UKP'] as const

/** True kalau string termasuk departemen valid (salah satu dari daftar tetap). */
export function isValidDepartment(value: unknown): value is Department {
  return typeof value === 'string' && (DEPARTMENTS as readonly string[]).includes(value)
}

// Warna badge per departemen (selaras tema). Dipakai untuk chip identitas user.
export interface DeptColor { bg: string; text: string; border: string }
export const DEPARTMENT_COLORS: Record<string, DeptColor> = {
  'HOLDING':     { bg: '#F1F1EF', text: '#1A1916', border: '#D6D5CF' }, // hitam
  'BISNIS & DEVELOPMENT': { bg: '#EAF6F6', text: '#0F766E', border: '#BFE3E1' }, // teal
  'HR':          { bg: '#FDF0EC', text: '#C2410C', border: '#F6CDBD' }, // terakota
  'FINANCE':     { bg: '#EDF6F0', text: '#15803D', border: '#C8E4D2' }, // hijau
  'HAI-X':       { bg: '#F3EEFF', text: '#6D28D9', border: '#DDD0FA' }, // ungu
  'PADEL PARTY': { bg: '#EFF4FF', text: '#1D4ED8', border: '#C7D7FE' }, // biru
  'UIM':         { bg: '#FEF9E7', text: '#A16207', border: '#FBE6A2' }, // kuning
  'UKP':         { bg: '#ECEFF8', text: '#1E3A8A', border: '#C7D0EA' }, // navy
}
const NEUTRAL_COLOR: DeptColor = { bg: '#F4F3F0', text: '#6B6860', border: '#E0DFD8' }

/** Warna untuk departemen tertentu (fallback netral kalau tak dikenal/null). */
export function deptColor(department: string | null | undefined): DeptColor {
  return (department && DEPARTMENT_COLORS[department]) || NEUTRAL_COLOR
}

// Warna AKSEN PROJECT per departemen (border kartu, progress bar, dsb).
// Identitas: HOLDING hitam, HAI-X ungu, UIM kuning, PADEL PARTY biru.
export const DEPARTMENT_PROJECT_COLORS: Record<string, string> = {
  'HOLDING':     '#1A1916', // hitam
  'BISNIS & DEVELOPMENT': '#0F766E', // teal
  'HR':          '#C2410C', // terakota
  'FINANCE':     '#15803D', // hijau
  'HAI-X':       '#7C3AED', // ungu
  'UIM':         '#EAB308', // kuning
  'PADEL PARTY': '#2563EB', // biru
  'UKP':         '#1E3A8A', // navy
}
/** Warna default project bila departemen tak dikenal/null. */
export const DEFAULT_PROJECT_COLOR = '#D4A843'

/** Warna aksen project berdasarkan departemen pemiliknya. */
export function projectColorForDept(department: string | null | undefined): string {
  return (department && DEPARTMENT_PROJECT_COLORS[department]) || DEFAULT_PROJECT_COLOR
}
